import { Button } from "@/components/ui/button";
import { PROPOSAL_BG, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { useEffect } from "react";

export type ProposalSupersededPageProps = {
  proposalRef?: string | null;
  /** Canonical customer-visible / accepted revision reference (preferred for display). */
  latestProposalRef?: string | null;
  latestProposalPath: string | null;
  salespersonName?: string | null;
};

/**
 * Full-page Link B block when a newer customer-visible revision exists.
 * No old commercial content is mounted alongside this screen.
 * Customers choose "View Updated Proposal" — do not auto-navigate away from this notice.
 */
export default function ProposalSupersededPage({
  proposalRef,
  latestProposalRef,
  latestProposalPath,
  salespersonName,
}: ProposalSupersededPageProps) {
  useEffect(() => {
    // Discourage bfcache restoring a previously rendered old proposal.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    try {
      // Hint intermediaries / shared caches when possible (meta is advisory for documents).
      let meta = document.querySelector('meta[http-equiv="Cache-Control"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("http-equiv", "Cache-Control");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", "no-store, no-cache, must-revalidate");
    } catch {
      // ignore
    }
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const destination = latestProposalPath?.trim() || "";
  const hasDestination = Boolean(destination);
  const identifiedRef = String(latestProposalRef ?? "").trim() || String(proposalRef ?? "").trim();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
      style={{ background: PROPOSAL_BG, color: PROPOSAL_TEXT }}
      role="main"
      aria-labelledby="proposal-superseded-heading"
    >
      <div className="max-w-md w-full text-center space-y-5">
        <h1
          id="proposal-superseded-heading"
          className="text-2xl font-light"
          style={{ fontFamily: PROPOSAL_COPY_FONT }}
        >
          This proposal has been updated
        </h1>
        <p
          className="text-sm text-[#1A2744]/80 leading-relaxed"
          style={{ fontFamily: PROPOSAL_COPY_FONT }}
        >
          A newer version of this proposal is available.
        </p>
        {identifiedRef ? (
          <p className="text-xs tracking-wider text-[#1A2744]/50">Reference: {identifiedRef}</p>
        ) : null}
        {hasDestination ? (
          <Button
            asChild
            className="w-full sm:w-auto min-h-11 px-8 text-base font-medium text-white"
            style={{ background: PROPOSAL_TEXT }}
          >
            <a href={destination} aria-label="View updated proposal">
              View Updated Proposal
            </a>
          </Button>
        ) : (
          <p
            className="text-sm text-[#1A2744]/80 leading-relaxed"
            style={{ fontFamily: PROPOSAL_COPY_FONT }}
          >
            Please contact{salespersonName?.trim() ? ` ${salespersonName.trim()} at` : ""} Clarity for
            the latest proposal.
          </p>
        )}
      </div>
    </div>
  );
}
